import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { getPageByRoute } from '../../services/pagesService';
import PageBlocks from './PageBlocks';
import Header from './Header';
import Footer from './Footer';

/**
 * La page de la base si elle est publiée, l'ancienne sinon.
 *
 * ========================================================================
 * POURQUOI CE COMPOSANT EXISTE
 * ========================================================================
 *
 * Faire servir une page de la base demandait jusqu'ici de modifier App.jsx,
 * reconstruire et redéployer — un geste de développeur pour une décision
 * d'éditeur. Et le basculement était à sens unique : `/install` a été basculé
 * avant que le rendu par blocs n'égale la page soignée, et revenir en arrière
 * demandait un nouveau déploiement.
 *
 * Désormais, LA PUBLICATION EST L'INTERRUPTEUR :
 *
 *   page publiée sur cette route  →  elle s'affiche
 *   page dépubliée, ou absente    →  l'ancienne reprend la main
 *
 * On peut donc publier, comparer les deux rendus, et dépublier si le résultat
 * ne convient pas — le visiteur retrouve la page d'origine immédiatement.
 *
 * ========================================================================
 * CE QUE L'ON AFFICHE PENDANT L'INTERROGATION
 * ========================================================================
 *
 * Le repli, pas un écran de chargement.
 *
 * Un « chargement… » ferait clignoter toutes les pages concernées à chaque
 * visite, pour une bascule qui reste l'exception. Afficher d'emblée
 * l'ancienne page ne coûte rien quand elle gagne — le cas courant — et
 * produit un remplacement bref quand la base l'emporte.
 *
 * ========================================================================
 * UNE PANNE DE L'API NE DOIT RIEN CASSER
 * ========================================================================
 *
 * Si l'appel échoue, on garde le repli. Une page de contenu indisponible vaut
 * mieux qu'une page blanche, et c'est la raison d'avoir gardé les composants
 * d'origine plutôt que de les supprimer.
 *
 * ========================================================================
 * `attendre` : QUAND LE REPLI NE PEUT PAS ÊTRE REMPLACÉ SOUS LES DOIGTS
 * ========================================================================
 *
 * La page d'accueil porte un formulaire de connexion. Afficher le repli puis
 * le remplacer par la page de la base ferait perdre au visiteur l'adresse
 * qu'il a commencé à taper. Avec `attendre` :
 *
 *   tant que l'API n'a pas répondu   →  un écran blanc, rien d'interactif
 *   réponse (page ou rien) à temps   →  la page, ou le repli
 *   erreur de l'API                  →  le repli, tout de suite
 *   DELAI_ATTENTE_MS écoulé          →  le repli, ET PLUS AUCUNE BASCULE :
 *                                        une réponse tardive est ignorée
 *
 * Le délai borne le prix d'une API lente : au pire une seconde et demie de
 * blanc, jamais une page qui ne vient pas. Les autres routes n'attendent
 * pas : elles n'ont rien à perdre au remplacement.
 */
export const DELAI_ATTENTE_MS = 1500;

export default function PageOuRepli({ route, repli: Repli, propsRepli = {}, attendre = false }) {
    const { language } = useTranslation();
    const [page, setPage] = useState(null);
    const [enAttente, setEnAttente] = useState(Boolean(attendre));

    // Posé quand le délai a monté le repli : plus rien ne doit le remplacer.
    // Une référence et non un état : la réponse tardive le lit dans une
    // promesse, après coup, et doit voir la valeur du moment.
    const repliDefinitif = useRef(false);

    useEffect(() => {
        let annule = false;
        let minuteur = null;

        // Seule la PREMIÈRE résolution se fait attendre. Un changement de
        // langue laisse affiché ce qui l'est jusqu'à la nouvelle réponse.
        if (enAttente) {
            minuteur = setTimeout(() => {
                repliDefinitif.current = true;
                setEnAttente(false);
            }, DELAI_ATTENTE_MS);
        }

        const resoudre = async () => {
            let trouvee = null;
            try {
                trouvee = await getPageByRoute(route, language);
            } catch (e) {
                // On garde le repli : voir l'en-tête.
                trouvee = null;
            }

            if (annule || repliDefinitif.current) return;

            clearTimeout(minuteur);
            setPage(trouvee);
            setEnAttente(false);
        };

        resoudre();
        return () => {
            annule = true;
            clearTimeout(minuteur);
        };
        // enAttente n'est lu qu'au lancement : le relire relancerait l'appel
        // au moment même où il aboutit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route, language]);

    if (enAttente) {
        // Ni en-tête ni pied de page : ce qui s'afficherait ensuite n'est pas
        // encore connu, et un en-tête qui change de forme clignoterait. Le
        // fond blanc est celui de l'accueil, dans les deux cas.
        return <div className="min-h-screen bg-white" />;
    }

    if (!page) {
        return <Repli {...propsRepli} />;
    }

    // LE TITRE DE LA PAGE N'EST PAS AFFICHE DEUX FOIS.
    //
    // Un bloc « hero » porte deja le titre de couverture. L'ajouter au-dessus
    // donnait « Installer l'application » suivi de « Installez l'application
    // Cabaza » — deux titres qui disent la meme chose, dont l'un vient du
    // champ `title` et l'autre du contenu.
    //
    // Le champ `title` sert alors ce a quoi il est fait : l'onglet du
    // navigateur, les listes de l'admin, le referencement. Sans hero, il
    // reste affiche, sans quoi la page commencerait sans en-tete.
    const aUnHero = Array.isArray(page.blocs) && page.blocs.some(b => b?.type === 'hero');

    // LA PAGE D'ACCUEIL : l'enveloppe de LandingPage, classe pour classe, quelle
    // que soit la mise en page choisie. Le bloc « accueilConnexion » pose sa
    // largeur (max-w-7xl) et ses marges, et porte le h1 : dans une carte ou une
    // colonne, il serait comprime ; en pleine largeur, le flex-col pousserait
    // le pied de page en bas de l'ecran, la ou LandingPage le laisse suivre le
    // contenu. `relative` est celui de la page d'origine.
    const estAccueil = Array.isArray(page.blocs) && page.blocs.some(b => b?.type === 'accueilConnexion');

    if (estAccueil) {
        return (
            <div className="min-h-screen bg-white relative">
                <Header {...propsRepli} />
                <main>
                    <PageBlocks blocs={page.blocs} langue={language} />
                </main>
                <Footer />
            </div>
        );
    }

    // PLEINE LARGEUR : l'enveloppe de PricingPage, pas celle des pages
    // juridiques. Fond blanc, pas de carte — chaque bloc pose sa largeur
    // (max-w-7xl pour la grille des formules, bande grise d'un bord a
    // l'autre pour les moyens de paiement). Dans la carte de 896 px, quatre
    // formules cote a cote ne tenaient pas.
    //
    // LA LANGUE EST TRANSMISE AUX BLOCS. Elle manquait : le bloc tarif
    // formatait toujours en francais, et les noms des formules seraient
    // restes en francais sur la page anglaise.
    if (page.miseEnPage === 'pleine') {
        return (
            <div className="min-h-screen bg-white flex flex-col">
                <Header {...propsRepli} />
                <main className="flex-1">
                    {!aUnHero && (
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
                            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">{page.title}</h1>
                        </div>
                    )}
                    <PageBlocks blocs={page.blocs} langue={language} />
                </main>
                <Footer />
            </div>
        );
    }

    // LA COLONNE SUR FOND GRIS : l'enveloppe de InstallAppPage, classe pour
    // classe. Pas de carte blanche — les blocs y posent leurs propres cartes
    // sur le gris, comme la page d'origine —, et les marges de sa colonne
    // (sm:px-6 lg:px-8), plus larges que celles de la carte.
    //
    // PAS DE flex-col NON PLUS : le repli n'en a pas, et le pied de page
    // suit le contenu au lieu d'etre pousse en bas de l'ecran.
    //
    // [&>*:last-child]:mb-0 : chaque bloc porte sa marge basse (mb-10,
    // mb-16...). Le dernier l'ajoutait au py-12 du conteneur et creusait le
    // bas de page ; la FAQ du repli, elle, finit sur le py-12 seul.
    if (page.miseEnPage === 'colonne') {
        return (
            <div className="min-h-screen bg-gray-50">
                <Header {...propsRepli} />
                <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 [&>*:last-child]:mb-0">
                    {!aUnHero && (
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">{page.title}</h1>
                    )}
                    <PageBlocks blocs={page.blocs} langue={language} />
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header {...propsRepli} />
            <main className="flex-grow max-w-4xl mx-auto px-4 py-12 w-full">
                <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
                    {!aUnHero && (
                        <h1 className="text-3xl font-bold text-gray-900 mb-8">{page.title}</h1>
                    )}
                    <PageBlocks blocs={page.blocs} langue={language} />
                </div>
            </main>
            <Footer />
        </div>
    );
}
